import type { ConversationPersistence } from '@/services/types'
import {
  getConfiguredBot,
  getConfiguredBots,
  getDefaultConversationMode,
  getSurfaceExperienceConfig,
  getRuntimeContext,
  type ChatSurface,
  type ConfiguredBot,
  type LaunchContext,
  type RuntimeContext,
  type BotCatalogScope,
  type SessionScope,
  type UiPolicyPreset,
} from '@/config'
import { authSession } from '@/auth/session'

export interface AuthState {
  isAuthenticated: boolean
}

export interface ChatUiPolicy {
  showSessionSidebar: boolean
  showSettings: boolean
  showNewChat: boolean
  showSessionHistory: boolean
  allowSessionRename: boolean
  allowSessionDelete: boolean
  allowBotSwitching: boolean
  showLoginControls: boolean
}

export interface ResolvedChatContext {
  authState: AuthState
  runtimeContext: RuntimeContext
  bot?: ConfiguredBot
  access: 'allowed' | 'requires_auth'
  botCatalogScope: BotCatalogScope
  sessionScope: SessionScope
  conversationMode: ConversationPersistence
  historyEnabled: boolean
  botSelectionEnabled: boolean
  uiPolicy: ChatUiPolicy
}

export interface ResolveChatContextOptions {
  botId?: string | null
  authState?: AuthState
  surface?: ChatSurface
  launchContext?: LaunchContext
}

function resolvePreferredConversationMode(
  bot: ConfiguredBot | undefined,
  runtimeContext: RuntimeContext,
  authState: AuthState,
): ConversationPersistence {
  return getSurfaceExperienceConfig({ surface: runtimeContext.surface, bot }).conversationMode
    ?? getDefaultConversationMode(runtimeContext.surface, authState)
}

function resolveSessionScope(bot: ConfiguredBot | undefined, runtimeContext: RuntimeContext): SessionScope {
  if (runtimeContext.launchContext === 'lti_assignment') {
    return 'fixed_session'
  }
  return getSurfaceExperienceConfig({ surface: runtimeContext.surface, bot }).sessionScope
    ?? (
      runtimeContext.surface === 'helper_embed' || runtimeContext.surface === 'canvas_bot'
        ? 'bot_only'
        : 'user_all'
    )
}

function resolveBotCatalogScope(bot: ConfiguredBot | undefined, runtimeContext: RuntimeContext): BotCatalogScope {
  if (runtimeContext.launchContext === 'lti_assignment') {
    return 'single_bot'
  }
  return getSurfaceExperienceConfig({ surface: runtimeContext.surface, bot }).botCatalogScope
    ?? (
      runtimeContext.surface === 'canvas_workspace'
        ? 'academic_only'
        : runtimeContext.surface === 'desktop_workspace'
          ? 'all_allowed'
          : 'single_bot'
    )
}

function resolveUiPolicyPreset(bot: ConfiguredBot | undefined, runtimeContext: RuntimeContext): UiPolicyPreset {
  if (runtimeContext.launchContext === 'lti_assignment') {
    return 'assignment_locked'
  }
  return getSurfaceExperienceConfig({ surface: runtimeContext.surface, bot }).uiPolicy
    ?? (
      runtimeContext.surface === 'helper_embed'
        ? 'embedded_helper'
        : runtimeContext.surface === 'canvas_workspace'
          ? 'canvas_workspace'
          : runtimeContext.surface === 'canvas_bot'
            ? 'canvas_student'
            : 'desktop_standard'
    )
}

function buildUiPolicy(preset: UiPolicyPreset): ChatUiPolicy {
  switch (preset) {
    case 'embedded_helper':
      return {
        showSessionSidebar: false,
        showSettings: false,
        showNewChat: true,
        showSessionHistory: false,
        allowSessionRename: false,
        allowSessionDelete: false,
        allowBotSwitching: false,
        showLoginControls: true,
      }
    case 'canvas_workspace':
      return {
        showSessionSidebar: true,
        showSettings: false,
        showNewChat: true,
        showSessionHistory: true,
        allowSessionRename: true,
        allowSessionDelete: true,
        allowBotSwitching: true,
        showLoginControls: false,
      }
    case 'canvas_student':
      return {
        showSessionSidebar: true,
        showSettings: false,
        showNewChat: true,
        showSessionHistory: true,
        allowSessionRename: true,
        allowSessionDelete: true,
        allowBotSwitching: false,
        showLoginControls: false,
      }
    case 'assignment_locked':
      return {
        showSessionSidebar: false,
        showSettings: false,
        showNewChat: false,
        showSessionHistory: false,
        allowSessionRename: false,
        allowSessionDelete: false,
        allowBotSwitching: false,
        showLoginControls: false,
      }
    case 'desktop_standard':
    default:
      return {
        showSessionSidebar: true,
        showSettings: true,
        showNewChat: true,
        showSessionHistory: true,
        allowSessionRename: true,
        allowSessionDelete: true,
        allowBotSwitching: true,
        showLoginControls: true,
      }
  }
}

export function resolveChatContext(options: ResolveChatContextOptions = {}): ResolvedChatContext {
  const authState = options.authState ?? authSession
  const runtimeContext = getRuntimeContext({
    surface: options.surface,
    launchContext: options.launchContext,
  })
  const configuredBots = getConfiguredBots()
  const bot = options.botId ? getConfiguredBot(options.botId) : undefined
  const access = bot?.requiresAuth && !authState.isAuthenticated ? 'requires_auth' : 'allowed'
  const botCatalogScope = resolveBotCatalogScope(bot, runtimeContext)
  const sessionScope = resolveSessionScope(bot, runtimeContext)
  const uiPolicy = buildUiPolicy(resolveUiPolicyPreset(bot, runtimeContext))

  let conversationMode = resolvePreferredConversationMode(bot, runtimeContext, authState)

  if (!authState.isAuthenticated && !(bot?.allowAnonymousHistory ?? false)) {
    conversationMode = 'ephemeral'
  }

  if (sessionScope === 'fixed_session' && conversationMode === 'persistent') {
    conversationMode = 'ephemeral'
  }

  if (runtimeContext.surface === 'helper_embed' && sessionScope === 'bot_only' && conversationMode === 'persistent' && !uiPolicy.showSessionHistory) {
    conversationMode = 'ephemeral'
  }

  const historyEnabled =
    access === 'allowed'
    && conversationMode === 'persistent'
    && uiPolicy.showSessionHistory
    && sessionScope !== 'fixed_session'
  const botSelectionEnabled = uiPolicy.allowBotSwitching && !bot?.isStatic && configuredBots.length !== 1

  return {
    authState,
    runtimeContext,
    bot,
    access,
    botCatalogScope,
    sessionScope,
    conversationMode,
    historyEnabled,
    botSelectionEnabled,
    uiPolicy,
  }
}
